import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Pencil, Plus, Trash2, Check, X } from 'lucide-react';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import { useInlineEdit } from '@/shared/components/custom/list-view/hooks/useInlineEdit';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import {
  useCreateClientContact,
  useDeleteClientContact,
  useUpdateClientContactMutation,
} from '@/features/client-contacts/public';
import type { ClientContact } from '@/features/client-contacts/public';
import type { ColumnDef } from '@tanstack/react-table';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClientContactsInlineTableProps {
  contacts: ClientContact[];
  onContactsChange: (contacts: ClientContact[]) => void;
  clientId?: string;
  isLoading?: boolean;
}

interface ContactRow extends ClientContact {
  id: string;
}

interface ContactErrors {
  email?: string;
}

interface ContactTableMeta {
  editingRowId: string | null;
  draft: Record<string, unknown>;
  updateDraft: (key: string, value: unknown) => void;
  isSaving: boolean;
  isLoading: boolean;
  isPendingDelete: boolean;
  onEdit: (row: ContactRow) => void;
  onDelete: (row: ContactRow) => void;
  onSave: () => void;
  onCancel: () => void;
  tc: TFunction;
  t: TFunction;
  errors: ContactErrors;
  setError: (field: keyof ContactErrors, msg: string | undefined) => void;
}

function getMeta(table: { options: { meta?: unknown } }): ContactTableMeta {
  return table.options.meta as ContactTableMeta;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

const PHONE_NAV_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Enter',
  'Escape',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);

// ─── Actions cell (module-level, stable reference) ───────────────────────────

interface ContactActionsCellProps {
  isEditing: boolean;
  isSaving: boolean;
  isLoading: boolean;
  isPendingDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onSave: () => void;
  onCancel: () => void;
  t: TFunction;
}

function ContactActionsCell({
  isEditing,
  isSaving,
  isLoading,
  isPendingDelete,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  t,
}: ContactActionsCellProps) {
  if (isEditing) {
    return (
      <div className="flex items-center justify-end gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onSave}
              disabled={isSaving || isLoading}
              data-testid="client-contacts-save-button"
            >
              <Check className="h-4 w-4 text-green-600" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('contacts.tooltip.save')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onCancel}
              disabled={isSaving}
              data-testid="contact-cancel-btn"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('contacts.tooltip.cancel')}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={onEdit}
            disabled={isLoading}
            data-testid="contact-edit-btn"
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('contacts.tooltip.edit')}</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={onDelete}
            disabled={isLoading || isPendingDelete}
            data-testid="contact-delete-btn"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('contacts.tooltip.delete')}</TooltipContent>
      </Tooltip>
    </div>
  );
}

// ─── Stable column definitions ───────────────────────────────────────────────

const contactColumns: ColumnDef<ContactRow, unknown>[] = [
  {
    accessorKey: 'name',
    header: ({ table }) => getMeta(table).tc('table.name'),
    meta: { cellClassName: 'flex-1 min-w-0' },
    cell: ({ row, table }) => {
      const m = getMeta(table);
      const isEditing = row.original.id === m.editingRowId;
      if (isEditing) {
        return (
          <Input
            value={String(m.draft.name ?? '')}
            onChange={(e) => m.updateDraft('name', e.target.value)}
            placeholder={m.tc('dialog.namePlaceholder')}
            className="h-8"
            disabled={m.isLoading}
            data-testid="contact-name-input"
          />
        );
      }
      return (
        <span className="block truncate" title={row.original.name}>
          {row.original.name}
        </span>
      );
    },
  },
  {
    accessorKey: 'email',
    header: ({ table }) => getMeta(table).tc('table.email'),
    meta: { cellClassName: 'flex-1 min-w-0' },
    cell: ({ row, table }) => {
      const m = getMeta(table);
      const isEditing = row.original.id === m.editingRowId;
      if (isEditing) {
        return (
          <div className="flex flex-col gap-0.5">
            <Input
              value={String(m.draft.email ?? '')}
              onChange={(e) => {
                m.updateDraft('email', e.target.value);
                if (m.errors.email) m.setError('email', undefined);
              }}
              onBlur={() => {
                const val = String(m.draft.email ?? '');
                if (val && !EMAIL_REGEX.test(val)) {
                  m.setError('email', m.tc('errors.emailInvalid'));
                }
              }}
              placeholder={m.tc('dialog.emailPlaceholder')}
              type="email"
              className="h-8"
              disabled={m.isLoading}
              data-testid="contact-email-input"
            />
            {m.errors.email && (
              <p className="text-xs text-destructive leading-none">{m.errors.email}</p>
            )}
          </div>
        );
      }
      return (
        <span className="block truncate" title={row.original.email || undefined}>
          {row.original.email || '-'}
        </span>
      );
    },
  },
  {
    accessorKey: 'phone',
    header: ({ table }) => getMeta(table).tc('table.phone'),
    meta: { cellClassName: 'flex-1 min-w-0' },
    cell: ({ row, table }) => {
      const m = getMeta(table);
      const isEditing = row.original.id === m.editingRowId;
      if (isEditing) {
        return (
          <Input
            value={String(m.draft.phone ?? '')}
            onChange={(e) => m.updateDraft('phone', e.target.value)}
            onKeyDown={(e) => {
              if (e.ctrlKey || e.metaKey || e.altKey) return;
              if (PHONE_NAV_KEYS.has(e.key)) return;
              if (!/[\d\s+\-()]/u.test(e.key)) e.preventDefault();
            }}
            placeholder={m.tc('dialog.phonePlaceholder')}
            type="tel"
            inputMode="tel"
            className="h-8"
            disabled={m.isLoading}
            data-testid="contact-phone-input"
          />
        );
      }
      return (
        <span className="block truncate" title={row.original.phone || undefined}>
          {row.original.phone || '-'}
        </span>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    meta: { cellClassName: 'w-20' },
    cell: ({ row, table }) => {
      const m = getMeta(table);
      return (
        <ContactActionsCell
          isEditing={row.original.id === m.editingRowId}
          isSaving={m.isSaving}
          isLoading={m.isLoading}
          isPendingDelete={m.isPendingDelete}
          onEdit={() => m.onEdit(row.original)}
          onDelete={() => m.onDelete(row.original)}
          onSave={m.onSave}
          onCancel={m.onCancel}
          t={m.t}
        />
      );
    },
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const EMPTY_DRAFT = { name: '', email: '', phone: '', isPrimary: false };

export function ClientContactsInlineTable({
  contacts,
  onContactsChange,
  clientId,
  isLoading = false,
}: ClientContactsInlineTableProps) {
  const { t } = useTranslation('clients');
  const { t: tc } = useTranslation('clientContacts');
  const isEditMode = !!clientId;

  const createContact = useCreateClientContact(clientId ?? '');
  const deleteContact = useDeleteClientContact(clientId ?? '');
  const updateContact = useUpdateClientContactMutation(clientId ?? '');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  const [contactErrors, setContactErrors] = useState<ContactErrors>({});

  const handleSave = async (draft: Record<string, unknown>, rowId: string | null) => {
    const emailValue = String(draft.email ?? '');
    if (emailValue && !EMAIL_REGEX.test(emailValue)) {
      setContactErrors({ email: tc('errors.emailInvalid') });
      return;
    }
    setContactErrors({});

    if (rowId !== null && isEditMode) {
      await updateContact.mutateAsync({
        contactId: rowId,
        payload: {
          name: String(draft.name ?? ''),
          email: emailValue || undefined,
          phone: String(draft.phone ?? '') || undefined,
        },
      });
    } else if (rowId === null && isEditMode) {
      await createContact.mutateAsync({
        name: String(draft.name ?? ''),
        email: emailValue || undefined,
        phone: String(draft.phone ?? '') || undefined,
        isPrimary: Boolean(draft.isPrimary),
      });
    } else if (rowId !== null) {
      onContactsChange(
        contacts.map((c) =>
          c.id === rowId
            ? {
                ...c,
                name: String(draft.name ?? ''),
                email: emailValue,
                phone: String(draft.phone ?? ''),
              }
            : c,
        ),
      );
    } else {
      const newContact: ClientContact = {
        id: `temp-${Date.now()}`,
        name: String(draft.name ?? ''),
        email: emailValue,
        phone: String(draft.phone ?? ''),
        isPrimary: Boolean(draft.isPrimary),
        createdAt: new Date().toISOString(),
      };
      onContactsChange([...contacts, newContact]);
    }
  };

  const { meta, pendingRow, isSaving, startAdd } = useInlineEdit<ContactRow>({
    onSave: handleSave,
    onDelete: (row) => {
      if (!isEditMode) onContactsChange(contacts.filter((c) => c.id !== row.id));
    },
    emptyDraft: EMPTY_DRAFT,
    addLabel: tc('dialog.addContact'),
  });

  const [prevEditingRowId, setPrevEditingRowId] = useState<string | null>(null);
  // Clear errors when editing row changes — inline sync avoids useEffect
  if (prevEditingRowId !== (meta.editingRowId ?? null)) {
    setPrevEditingRowId(meta.editingRowId ?? null);
    setContactErrors({});
  }

  const handleDeleteRow = async (row: ContactRow) => {
    if (isEditMode) {
      const result = await confirm({
        title: t('contacts.confirm.deleteTitle'),
        description: t('contacts.confirm.deleteDesc', { name: row.name }),
        confirmLabel: t('actions.delete'),
        cancelLabel: t('confirm.cancel'),
        variant: 'destructive',
      });
      if (!result.confirmed) return;
      deleteContact.mutate(row.id);
    } else {
      onContactsChange(contacts.filter((c) => c.id !== row.id));
    }
  };

  const tableMetaRef = useRef<ContactTableMeta>({} as ContactTableMeta);
  // Intentional render-phase ref mutation: keeps contactColumns stable (module-level constant)
  // so TanStack Table never recreates columns, which would unmount edit inputs and lose focus.
  /* eslint-disable react-hooks/refs */
  tableMetaRef.current.editingRowId = meta.editingRowId;
  tableMetaRef.current.draft = meta.draft;
  tableMetaRef.current.updateDraft = meta.updateDraft;
  tableMetaRef.current.isSaving = isSaving;
  tableMetaRef.current.isLoading = isLoading;
  tableMetaRef.current.isPendingDelete = deleteContact.isPending;
  tableMetaRef.current.onEdit = (row) =>
    meta.startEdit(row.id, row as unknown as Record<string, unknown>);
  tableMetaRef.current.onDelete = handleDeleteRow;
  tableMetaRef.current.onSave = meta.save;
  tableMetaRef.current.onCancel = meta.cancelEdit;
  tableMetaRef.current.tc = tc;
  tableMetaRef.current.t = t;
  tableMetaRef.current.errors = contactErrors;
  tableMetaRef.current.setError = (field, msg) =>
    setContactErrors((prev) => ({ ...prev, [field]: msg }));
  const tableMetaProp = tableMetaRef.current as unknown as Record<string, unknown>;
  /* eslint-enable react-hooks/refs */

  const allRows = pendingRow ? [...contacts, pendingRow] : contacts;

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">{t('dialog.contacts')}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="default"
              size="icon"
              className="h-7 w-7"
              onClick={startAdd}
              disabled={meta.editingRowId !== null}
              data-testid="client-contacts-add-button"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{tc('dialog.addContact')}</TooltipContent>
        </Tooltip>
      </div>
      <ListView
        data={allRows}
        columns={contactColumns}
        editingRowId={meta.editingRowId}
        meta={tableMetaProp}
        className="border-border/50"
      />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
